// @ts-strict-ignore
import { TextSizeCheckManager } from '../manager/text-size-check.manager';
import { ContainerService } from '../model3d/container.service';
import { ArticleContainer } from '../model3d/article-container';
import { TextSizeCheckRequestVO } from '../manager/text-size-check.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { Connection } from '../model3d/connection';
import { CompressionSimpleTextValueVO, MetaElementUsageVO, SimpleTextValueVO, TableTextValueVO, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { LabelingUtils } from '../model3d/util/labeling-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { LoadConnectionTextureProcessor } from './load-connection-texture.processor';
import { Injectable } from '@angular/core';
import { ProjectChangedType } from '@project/model/project-changed';
import { ProjectModel } from '@project/model/project.model';

@Injectable({ providedIn: 'root' })
export class UpdateLabelingCompressionProcessor {
  private isActive: boolean = false;
  private connectionCubes: Connection[] = [];

  constructor(
    private textSizeCheckManager: TextSizeCheckManager,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private containerService: ContainerService,
    private labelingUtils: LabelingUtils,
    private projectModel: ProjectModel
  ) {}

  public async updateTextures(): Promise<void> {
    this.projectModel.sendProjectChanged(ProjectChangedType.TexturesUpdated);
    await this.checkCompressionValues();
  }

  private async checkCompressionValues(): Promise<void> {
    this.clear();
    const articleContainers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    const connectionCubesToRefresh: Connection[] = this.collectConnectionCubesWithoutCompression(articleContainers);
    if (ArrayUtils.isEmpty(connectionCubesToRefresh)) {
      return;
    }
    this.isActive = true;
    this.connectionCubes = connectionCubesToRefresh;
    const textSizeCheckRequests: TextSizeCheckRequestVO[] = this.textSizeCheckManager.buildTextSizeCheckRequest(connectionCubesToRefresh);
    if (textSizeCheckRequests.length > 0) {
      if (await this.textSizeCheckManager.retrieveTextSizeCheckInfos(textSizeCheckRequests)) {
        this.handleTextSizeCheckManagerRetrievalSuccess();
      } else {
        this.handleTextSizeCheckManagerRetrievalFailure();
      }
    } else {
      this.updateLabeling();
    }
  }

  private updateLabeling(): void {
    if (ArrayUtils.isNotEmpty(this.connectionCubes)) {
      for (const connection of this.connectionCubes) {
        connection.resetTexture();
      }
      const usageIdsToRefresh: string[] = this.recreateTextValuesForTextureAreas(this.connectionCubes);
      this.refreshLabelingArticles(usageIdsToRefresh);
    }
    this.clear();
  }

  private handleTextSizeCheckManagerRetrievalSuccess(): void {
    if (!this.isActive) {
      return;
    }
    this.updateLabeling();
  }

  private handleTextSizeCheckManagerRetrievalFailure(): void {
    if (!this.isActive) {
      return;
    }
    this.clear();
  }

  private recreateTextValuesForTextureAreas(connectionCubes: Connection[]): string[] {
    if (connectionCubes == null) {
      return [];
    }
    const processedArticleUsageIds: string[] = [];
    for (const cube of connectionCubes) {
      if (cube.metaElementUsage instanceof TextureAreaUsageVO && (cube.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId == null) {
        const textureAreaUsage: TextureAreaUsageVO = cube.metaElementUsage as TextureAreaUsageVO;
        let textureChanged: boolean = false;
        if (textureAreaUsage.textValue instanceof TableTextValueVO) {
          const tableText: TableTextValueVO = textureAreaUsage.textValue as TableTextValueVO;
          for (const row of tableText.rows) {
            let colSpanIndex: number = 0;
            for (const item of row.items) {
              let requiredCube: Connection = null;
              if (!(item.value instanceof CompressionSimpleTextValueVO)) {
                requiredCube = this.getMergedConnectionCubeByColSpan(cube, colSpanIndex);
              }
              colSpanIndex += item.colSpan;
              if (requiredCube == null) {
                continue;
              }
              item.value = this.createCompressionSimpleTextValue(item.value as SimpleTextValueVO);
              textureChanged = true;
            }
          }
        }
        if (textureChanged && processedArticleUsageIds.indexOf(textureAreaUsage.identity.articleUsageId) === -1) {
          processedArticleUsageIds.push(textureAreaUsage.identity.articleUsageId);
        }
      }
    }
    return processedArticleUsageIds;
  }

  private getMergedConnectionCubeByColSpan(baseCube: Connection, colSpanIndex: number): Connection {
    const mergedCubes: Connection[] = this.labelingUtils.findNextMergedTextureConnections(baseCube);
    return colSpanIndex < mergedCubes.length ? mergedCubes[colSpanIndex] : null;
  }

  private createCompressionSimpleTextValue(simpleValue: SimpleTextValueVO): CompressionSimpleTextValueVO {
    if (simpleValue == null) {
      return null;
    }
    const compressionTextValue: CompressionSimpleTextValueVO = new CompressionSimpleTextValueVO();
    compressionTextValue.text = simpleValue.text;
    compressionTextValue.hAlignment = simpleValue.hAlignment;
    compressionTextValue.vAlignment = simpleValue.vAlignment;
    compressionTextValue.rotation = simpleValue.rotation;
    compressionTextValue.bgColor = simpleValue.bgColor;
    return compressionTextValue;
  }

  private refreshLabelingArticles(labelingArticleUsageIds: string[]): void {
    if (labelingArticleUsageIds == null) {
      return;
    }
    const labelingContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(labelingArticleUsageIds);
    this.loadConnectionTextureProcessor.loadAndApplyImagesToConnectionsObservable(this.connectionCubes).subscribe(() => {
      for (const articleContainer of labelingContainers) {
        articleContainer.refreshTextureConnections();
      }
    });
  }

  private collectConnectionCubesWithoutCompression(articleContainers: ArticleContainer[]): Connection[] {
    if (articleContainers == null) {
      return [];
    }
    let cubes: Connection[] = [];
    for (const articleContainer of articleContainers) {
      if (!ArticleUtils.isLabelingArticle(articleContainer.article)) {
        continue;
      }
      const textureAreas: TextureAreaVO[] = ArticleUtils.getTextTextureElements(articleContainer.article);
      for (const textureArea of textureAreas) {
        const cube: Connection = articleContainer.findFirstConnectionByName(textureArea.name);
        if (cube == null) {
          continue;
        }
        if (this.isUpdateCompressionNeeded(cube.metaElementUsage)) {
          cubes = cubes.concat(this.labelingUtils.findNextMergedTextureConnections(cube));
        }
      }
    }
    return cubes;
  }

  private isUpdateCompressionNeeded(metaUsage: MetaElementUsageVO): boolean {
    if (metaUsage == null || !(metaUsage instanceof TextureAreaUsageVO)) {
      return false;
    }
    const textureUsage: TextureAreaUsageVO = metaUsage as TextureAreaUsageVO;
    if (textureUsage.previousMergedUsageId != null) {
      return false;
    }
    if (textureUsage.textValue instanceof TableTextValueVO) {
      for (const row of (textureUsage.textValue as TableTextValueVO).rows) {
        for (const item of row.items) {
          if (!(item.value instanceof CompressionSimpleTextValueVO)) {
            return true;
          }
        }
      }
    }
    return true;
  }

  private clear(): void {
    this.isActive = false;
    this.connectionCubes = [];
  }
}
